import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';
import { Layout } from './components/Layout';
import { TutorialModal } from './components/TutorialModal';

// Eager load critical pages (immediately needed)
import { Landing } from './pages/Landing';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import NotFound from './pages/NotFound';

// Lazy load non-critical pages (code splitting)
// Using named export wrapper for React.lazy compatibility
const Bots = lazy(() => import('./pages/Bots').then(m => ({ default: m.Bots })));
const BotDetails = lazy(() => import('./pages/BotDetails').then(m => ({ default: m.BotDetails })));
const BotSettings = lazy(() => import('./pages/BotSettings').then(m => ({ default: m.BotSettings })));
const Templates = lazy(() => import('./pages/Templates').then(m => ({ default: m.Templates })));
const Services = lazy(() => import('./pages/Services').then(m => ({ default: m.Services })));
const Transactions = lazy(() => import('./pages/Transactions').then(m => ({ default: m.Transactions })));
const AIIC = lazy(() => import('./pages/AIIC').then(m => ({ default: m.AIIC })));
const StockMarket = lazy(() => import('./pages/StockMarket').then(m => ({ default: m.StockMarket })));
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));
const Notifications = lazy(() => import('./pages/Notifications').then(m => ({ default: m.Notifications })));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then(m => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(m => ({ default: m.ResetPassword })));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail').then(m => ({ default: m.VerifyEmail })));
const Wiki = lazy(() => import('./pages/Wiki').then(m => ({ default: m.Wiki })));
const Terms = lazy(() => import('./pages/Terms').then(m => ({ default: m.Terms })));
const Privacy = lazy(() => import('./pages/Privacy').then(m => ({ default: m.Privacy })));
const Cookies = lazy(() => import('./pages/Cookies').then(m => ({ default: m.Cookies })));
const McpDocs = lazy(() => import('./pages/McpDocs').then(m => ({ default: m.McpDocs })));
const DMCA = lazy(() => import('./pages/DMCA').then(m => ({ default: m.DMCA })));
const Support = lazy(() => import('./pages/Support').then(m => ({ default: m.Support })));

import './index.css';
import { GoogleAnalytics } from './components/GoogleAnalytics';

// Loading fallback component
function PageLoader() {
  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
    </div>
  );
}

function PrivateLayout() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return user ? (
    <Layout>
      <Outlet />
      <TutorialModal />
    </Layout>
  ) : (
    <Navigate to="/login" />
  );
}

function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <Router>
            <GoogleAnalytics />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Admin routes - show 404 */}
                <Route path="/network" element={<NotFound />} />
                <Route path="/ethics" element={<NotFound />} />
                <Route path="/reputation" element={<NotFound />} />
                <Route path="/approvals" element={<NotFound />} />
                <Route path="/system" element={<NotFound />} />
                
                {/* Public routes - critical pages eager loaded */}
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                
                {/* Public routes - lazy loaded */}
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/wiki" element={<Wiki />} />
                <Route path="/mcp-docs" element={<McpDocs />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/dmca" element={<DMCA />} />
                <Route path="/support" element={<Support />} />
                
                {/* Private routes - keep layout mounted */}
                <Route element={<PrivateLayout />}>
                  {/* Private routes - critical pages eager loaded */}
                  <Route path="/dashboard" element={<Dashboard />} />
                  
                  {/* Private routes - lazy loaded */}
                  <Route path="/notifications" element={<Notifications />} />
                  <Route path="/bots" element={<Bots />} />
                  <Route path="/bots/:botId" element={<BotDetails />} />
                  <Route path="/bots/:botId/settings" element={<BotSettings />} />
                  <Route path="/templates" element={<Templates />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/transactions" element={<Transactions />} />
                  <Route path="/aiic" element={<AIIC />} />
                  <Route path="/stock-market" element={<StockMarket />} />
                  <Route path="/settings" element={<Settings />} />
                </Route>
                
                {/* Catch all - 404 */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </Router>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
